import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import InputForm from "../../componentes/input/input";
import { Loading } from "../../componentes/loading/loading";
import GrupoService from "../../services/resource/grupoService";
import UsuarioService from "../../services/resource/usuarioService";

const initialValues = {
    id: null,
    nome: "",
    descricao: "",
    usuariosIds: []
};

export default function GrupoUsuariosCrud() {
    const [grupos, setGrupos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [grupoForm, setGrupoForm] = useState(initialValues);
    const [grupoDelete, setGrupoDelete] = useState(null);
    const [usuarioSearchModal, setUsuarioSearchModal] = useState("");

    const grupoService = new GrupoService();
    const usuarioService = new UsuarioService();

    const isNovoGrupo = grupoForm.id === null || grupoForm.id === "";

    useEffect(() => {
        document.title = "MyDash | Grupos de Usuários";
        carregarGrupos();

        usuarioService.listarUsuarios()
            .then(res => setUsuarios(res.data))
            .catch(error => {
                console.error("Erro ao listar usuários", error);
                toast.error("Erro ao carregar usuários");
            });
    }, []);

    function carregarGrupos() {
        setLoading(true);
        grupoService.listarGrupos()
            .then(res => setGrupos(res.data))
            .catch(error => {
                console.error("Erro ao listar grupos", error);
                toast.error("Erro ao carregar grupos");
            })
            .finally(() => {
                setTimeout(() => setLoading(false), 400);
            });
    }

    const usuariosDisponiveis = useMemo(() => {
        const termo = usuarioSearchModal.toLowerCase();
        return usuarios
            .filter(u => !grupoForm.usuariosIds.includes(u.id))
            .filter(u =>
                !termo ||
                u.nome?.toLowerCase().includes(termo) ||
                u.email?.toLowerCase().includes(termo) ||
                u.nomeUsuario?.toLowerCase().includes(termo)
            );
    }, [usuarios, grupoForm.usuariosIds, usuarioSearchModal]);

    const usuariosAdicionados = useMemo(() =>
        usuarios.filter(u => grupoForm.usuariosIds.includes(u.id)),
        [usuarios, grupoForm.usuariosIds]
    );

    function adicionarUsuarioAoForm(usuarioId) {
        setGrupoForm(prev => ({ ...prev, usuariosIds: [...prev.usuariosIds, usuarioId] }));
    }

    function removerUsuarioDoForm(usuarioId) {
        setGrupoForm(prev => ({ ...prev, usuariosIds: prev.usuariosIds.filter(id => id !== usuarioId) }));
    }

    const gruposFiltrados = grupos.filter(g =>
        !search ||
        g.nome?.toLowerCase().includes(search.toLowerCase()) ||
        g.descricao?.toLowerCase().includes(search.toLowerCase())
    );

    function openModal(grupo = null) {
        if (grupo) {
            setGrupoForm({
                id: grupo.id,
                nome: grupo.nome,
                descricao: grupo.descricao,
                usuariosIds: (grupo.usuarios || []).map(u => u.id)
            });
        } else {
            setGrupoForm(initialValues);
        }
        setUsuarioSearchModal("");
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
    }

    function handleSave(e) {
        e.preventDefault();
        if (saving) return;

        const { id, nome, descricao, usuariosIds } = grupoForm;

        if (!nome) {
            toast.error("Informe o nome do grupo.");
            return;
        }

        const dados = { nome, descricao, usuariosIds };

        setSaving(true);
        const request = id
            ? grupoService.atualizarGrupo(id, dados)
            : grupoService.cadastrarGrupo(dados);

        request
            .then(() => {
                toast.success(id ? "Grupo atualizado!" : "Grupo cadastrado!");
                closeModal();
                carregarGrupos();
            })
            .catch(error => {
                console.error("Erro ao salvar grupo", error);
                toast.error("Erro ao salvar grupo");
            })
            .finally(() => setSaving(false));
    }

    function confirmDelete(grupo) {
        setGrupoDelete(grupo);
        setDeleteModalOpen(true);
    }

    function handleDelete() {
        grupoService.excluirGrupo(grupoDelete.id)
            .then(() => {
                toast.success("Grupo excluído!");
                setDeleteModalOpen(false);
                carregarGrupos();
            })
            .catch(error => {
                console.error("Erro ao excluir grupo", error);
                toast.error("Erro ao excluir grupo");
            });
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                {/* Header */}
                <div className="flex flex-col gap-4 bg-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-7">
                    <h1 className="font-display text-2xl font-bold text-primary-foreground">Grupos de Usuários</h1>
                    <button
                        onClick={() => openModal()}
                        className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-sm font-semibold text-primary transition-colors duration-150 hover:bg-muted"
                    >
                        <i className="fas fa-plus mr-2"></i> Novo Grupo
                    </button>
                </div>

                {/* Search */}
                <div className="border-b border-border bg-muted px-5 py-5 md:px-7">
                    <div className="relative w-full xl:w-72">
                        <input
                            type="text"
                            placeholder="Pesquisar grupos..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-input rounded-lg transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                        />
                        <i className="fas fa-search absolute left-3 top-3 text-muted-foreground"></i>
                    </div>
                </div>

                {/* Table */}
                {loading ?
                    <Loading />
                    :
                    <div className="border-b border-border">
                        <div className="overflow-x-auto fade-in">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header">
                                    <tr className="text-sm">
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Descrição</th>
                                        <th className="py-3 px-6 text-left text-xs font-medium text-table-header-fg uppercase tracking-wider">Usuários</th>
                                        <th className="py-3 px-6 text-right text-xs font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {gruposFiltrados.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-2 text-center text-muted-foreground">
                                                Nenhum grupo encontrado
                                            </td>
                                        </tr>
                                    ) : (
                                        gruposFiltrados.map(g => (
                                            <tr key={g.id} className="text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                <td className="px-6 py-3 whitespace-nowrap font-medium text-foreground">{g.nome}</td>
                                                <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{g.descricao || "-"}</td>
                                                <td className="px-6 py-3 max-w-xs">
                                                    <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                                                        {(g.usuarios || []).length === 0 ? (
                                                            <span className="text-muted-foreground">Nenhum usuário</span>
                                                        ) : (
                                                            <>
                                                                {g.usuarios.slice(0, 3).map(u => (
                                                                    <span key={u.id} className="shrink-0 max-w-[120px] truncate inline-flex rounded-md bg-primary/10 px-2 py-1 text-[10px] font-semibold text-primary" title={u.nome}>
                                                                        {u.nome}
                                                                    </span>
                                                                ))}
                                                                {g.usuarios.length > 3 && (
                                                                    <span
                                                                        className="shrink-0 inline-flex rounded-md bg-muted-foreground/10 px-2 py-1 text-[10px] font-semibold text-muted-foreground"
                                                                        title={g.usuarios.slice(3).map(u => u.nome).join(", ")}
                                                                    >
                                                                        +{g.usuarios.length - 3}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 whitespace-nowrap text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <i
                                                            onClick={() => openModal(g)}
                                                            className="fa fa-pencil-square text-primary hover:text-primary/80 p-1 rounded-full hover:bg-primary/10 cursor-pointer"
                                                        />
                                                        <i
                                                            onClick={() => confirmDelete(g)}
                                                            className="fa fa-times text-destructive hover:text-destructive/80 p-1 rounded-full hover:bg-destructive/10 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
            </div>

            {/* Modal Form */}
            <Dialog
                header={isNovoGrupo ? "Cadastro de Grupo" : "Atualizar Grupo"}
                onHide={closeModal}
                visible={modalOpen}
                blockScroll
                className="w-[95%] "
                footer={
                    <div className="flex justify-end space-x-3 mt-3">
                        <button type="button" onClick={closeModal} className="px-4 py-2 bg-card border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                        <button
                            type="submit"
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-primary hover:brightness-95 text-primary-foreground rounded-md transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin"></i>}
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </div>
                }
            >
                <form className="p-4" onSubmit={handleSave}>
                    <div className="grid grid-cols-2 gap-4">
                        <InputForm
                            type="text"
                            label="Nome do Grupo"
                            placeholder="Ex: Diretoria"
                            value={grupoForm.nome}
                            onChange={e => setGrupoForm({ ...grupoForm, nome: e.target.value })}
                            className="w-full mb-4 border px-3 py-2"
                        />
                        <InputForm
                            type="text"
                            label="Descrição"
                            placeholder="Descrição do grupo"
                            value={grupoForm.descricao}
                            onChange={e => setGrupoForm({ ...grupoForm, descricao: e.target.value })}
                            className="w-full mb-4 border px-3 py-2"
                        />
                    </div>
                    <div className="grid grid-cols-1">
                        <div className="mb-2 rounded-xl border border-border bg-muted p-4">
                            <label className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                                <i className="fa fa-user-plus text-primary"></i>
                                Usuários para adicionar
                            </label>

                            <div className="relative mb-3">
                                <input
                                    type="text"
                                    placeholder="Pesquisar usuários..."
                                    value={usuarioSearchModal}
                                    onChange={e => setUsuarioSearchModal(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-input rounded-lg bg-card transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                                />
                                <i className="fas fa-search absolute left-3 top-2.5 text-xs text-muted-foreground"></i>
                            </div>

                            <div className="border border-border rounded-lg overflow-hidden shadow-xs">
                                <div className="max-h-40 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-border">
                                        <thead className="bg-table-header sticky top-0">
                                            <tr>
                                                <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                                <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">E-mail</th>
                                                <th className="py-2 px-3 text-right text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border bg-card">
                                            {usuariosDisponiveis.length === 0 ? (
                                                <tr>
                                                    <td colSpan="3" className="px-3 py-3 text-xs text-center text-muted-foreground">Nenhum usuário encontrado</td>
                                                </tr>
                                            ) : (
                                                usuariosDisponiveis.map(u => (
                                                    <tr key={u.id} className="text-xs hover:bg-table-row-hover">
                                                        <td className="px-3 py-2 whitespace-nowrap font-medium text-foreground">{u.nome}</td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{u.email}</td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => adicionarUsuarioAoForm(u.id)}
                                                                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20 transition-colors duration-150"
                                                            >
                                                                <i className="fa fa-plus"></i> Adicionar
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4 rounded-xl border border-border bg-muted p-4">
                            <label className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                                <i className="fa fa-users text-primary"></i>
                                Usuários já adicionados
                                <span className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold px-2 py-0.5">
                                    {usuariosAdicionados.length}
                                </span>
                            </label>

                            <div className="border border-border rounded-lg overflow-hidden shadow-xs">
                                <div className="max-h-40 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-border">
                                        <thead className="bg-table-header sticky top-0">
                                            <tr>
                                                <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Nome</th>
                                                <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">E-mail</th>
                                                <th className="py-2 px-3 text-right text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border bg-card">
                                            {usuariosAdicionados.length === 0 ? (
                                                <tr>
                                                    <td colSpan="3" className="px-3 py-3 text-xs text-center text-muted-foreground">Nenhum usuário adicionado</td>
                                                </tr>
                                            ) : (
                                                usuariosAdicionados.map(u => (
                                                    <tr key={u.id} className="text-xs hover:bg-table-row-hover">
                                                        <td className="px-3 py-2 whitespace-nowrap font-medium text-foreground">{u.nome}</td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{u.email}</td>
                                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                                            <button
                                                                type="button"
                                                                onClick={() => removerUsuarioDoForm(u.id)}
                                                                className="inline-flex items-center gap-1 rounded-md bg-destructive/10 px-2 py-1 text-[11px] font-semibold text-destructive hover:bg-destructive/20 transition-colors duration-150"
                                                            >
                                                                <i className="fa fa-times"></i> Remover
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Dialog>

            {/* Delete Confirmation */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Tem certeza que deseja <strong>excluir</strong> o grupo: {grupoDelete?.nome}?</p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setDeleteModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={handleDelete} className="px-4 py-2 rounded-md transition-all duration-150 bg-destructive text-destructive-foreground hover:brightness-95">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
